import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Shield, Briefcase, Building2, ChevronRight, Calendar, Users, TrendingUp, Phone, ChevronLeft, Heart, Laptop, UtensilsCrossed, Home, Gem, ShoppingBag, Stethoscope, Car, BookOpen, Palette, Apple, Store } from "lucide-react";
import { SITE_INFO, SERVIZI_CATEGORIE, ASSOCIAZIONI } from "@/data/siteData";
import { NEWS_FULL } from "@/data/newsData";
import { EVENTI_FULL } from "@/data/eventiData";
import { ASSOCIAZIONI as ASSOC_LIST, MERCATO_HEX, BADGE_COLORS } from "@/data/associazioniList";

const ICON_MAP = { Shield, Briefcase, Building2 };

const HERO_SLIDES = [
  {
    image: "https://images.unsplash.com/photo-1515542622106-78bda8ba0e5b?w=1920&q=80&auto=format",
    alt: "Piazza del Campidoglio a Roma, architettura rinascimentale",
    overline: "Dal 1944 al fianco delle imprese",
    title: "Il punto di riferimento per le imprese di Roma",
    subtitle: "Commercio, turismo, servizi e professioni. Un hub moderno per la crescita della tua impresa.",
    cta: { label: "I nostri servizi", path: "/servizi" },
    ctaSecondary: { label: "Diventa socio", path: "/contatti" },
  },
  {
    image: "/hero-promoter.jpg",
    alt: "Promo.Ter - Formazione e servizi per il terziario di Confcommercio Roma",
    overline: "FORMAZIONE E SERVIZI",
    title: "Migliora le performance della tua Azienda con Promo.Ter",
    subtitle: "Esplora l'offerta di servizi e di formazione dedicata al tuo Business",
    cta: { label: "Promo.Ter", path: "/chi-siamo/enti-collegati/promo-ter" },
    ctaSecondary: { label: "Formazione", path: "/formazione" },
  },
  {
    image: "https://images.unsplash.com/photo-1556761175-4b46a572b786?w=1920&q=80&auto=format",
    alt: "Team di professionisti in riunione strategica in un ufficio moderno",
    overline: "Servizi per le imprese",
    title: "Consulenza, formazione e assistenza su misura",
    subtitle: "Dalla consulenza legale alla digitalizzazione: oltre 20 servizi dedicati per supportare ogni aspetto della tua attività.",
    cta: { label: "Scopri i servizi", path: "/servizi" },
    ctaSecondary: { label: "Contattaci", path: "/contatti" },
  },
  {
    image: "https://images.unsplash.com/photo-1531572753322-ad063cecc140?w=1920&q=80&auto=format",
    alt: "Panorama aereo di Roma con i tetti e le cupole della città",
    overline: "Il sistema Confcommercio Roma",
    title: "15 Associazioni di categoria unite per il territorio",
    subtitle: "Una rete capillare che rappresenta il commercio, il turismo, i servizi e le professioni della Capitale.",
    cta: { label: "Le associazioni", path: "/chi-siamo#associazioni" },
    ctaSecondary: { label: "Chi siamo", path: "/chi-siamo" },
  },
  {
    image: "https://images.unsplash.com/photo-1591115765373-5207764f72e7?w=1920&q=80&auto=format",
    alt: "Imprenditrice al lavoro con tablet in un negozio moderno",
    overline: "Innovazione e digitalizzazione",
    title: "Sportello Innovazione SPIN EDI",
    subtitle: "Supporto all'innovazione e alla trasformazione digitale delle PMI del terziario.",
    cta: { label: "Scopri SPIN EDI", path: "/servizi/sportello-spin-edi" },
    ctaSecondary: { label: "Formazione", path: "/formazione" },
  },
];

function HeroSection() {
  const [current, setCurrent] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const goTo = useCallback((idx) => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrent(idx);
    setTimeout(() => setIsTransitioning(false), 700);
  }, [isTransitioning]);

  const next = useCallback(() => goTo((current + 1) % HERO_SLIDES.length), [current, goTo]);
  const prev = useCallback(() => goTo((current - 1 + HERO_SLIDES.length) % HERO_SLIDES.length), [current, goTo]);

  // Touch swipe
  const [touchStart, setTouchStart] = useState(null);
  const handleTouchStart = (e) => setTouchStart(e.touches[0].clientX);
  const handleTouchEnd = (e) => {
    if (touchStart === null) return;
    const diff = touchStart - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) { diff > 0 ? next() : prev(); }
    setTouchStart(null);
  };

  useEffect(() => {
    const timer = setInterval(next, 6000);
    return () => clearInterval(timer);
  }, [next]);

  const slide = HERO_SLIDES[current];

  return (
    <section
      className="relative h-[600px] md:h-[680px] lg:h-[720px] overflow-hidden touch-pan-y"
      data-testid="hero-slider"
      aria-roledescription="carousel"
      aria-label="Slider principale"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* Background images - all preloaded */}
      {HERO_SLIDES.map((s, i) => (
        <div
          key={i}
          className="absolute inset-0 transition-opacity duration-700 ease-in-out"
          style={{ opacity: i === current ? 1 : 0 }}
          aria-hidden={i !== current}
        >
          <img
            src={s.image}
            alt={s.alt}
            className="absolute inset-0 w-full h-full object-cover"
            loading={i === 0 ? "eager" : "lazy"}
          />
          {/* Overlay gradient */}
          <div className="absolute inset-0" style={{ background: "linear-gradient(to right, rgba(4,30,66,0.92), rgba(4,30,66,0.70), rgba(4,30,66,0.30))" }} />
        </div>
      ))}

      {/* Content */}
      <div className="relative z-10 h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center">
        <div className="max-w-2xl">
          <span
            key={`overline-${current}`}
            className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-[#56B7E6] mb-4 font-['Encode_Sans_Semi_Condensed'] animate-fadeSlideUp"
          >
            {slide.overline}
          </span>
          <h1
            key={`title-${current}`}
            className="text-4xl md:text-5xl lg:text-[3.5rem] font-bold text-white leading-[1.1] font-['Encode_Sans_Semi_Condensed'] tracking-tight mb-5 animate-fadeSlideUp"
            style={{ animationDelay: "100ms" }}
          >
            {slide.title}
          </h1>
          <p
            key={`sub-${current}`}
            className="text-base md:text-lg text-white/80 leading-relaxed mb-8 max-w-xl animate-fadeSlideUp"
            style={{ animationDelay: "200ms" }}
          >
            {slide.subtitle}
          </p>
          <div className="flex flex-wrap gap-4 animate-fadeSlideUp" style={{ animationDelay: "300ms" }}>
            <Link
              to={slide.cta.path}
              className="inline-flex items-center gap-2 bg-white text-[var(--confcom-blue)] px-7 py-3.5 rounded-md font-semibold hover:opacity-90 transition-opacity shadow-lg text-sm"
              data-testid="hero-cta-primary"
            >
              {slide.cta.label}
              <ArrowRight size={18} aria-hidden="true" />
            </Link>
            <Link
              to={slide.ctaSecondary.path}
              className="inline-flex items-center gap-2 border-2 border-white/40 text-white px-7 py-3.5 rounded-md font-semibold hover:opacity-90 transition-opacity text-sm"
              data-testid="hero-cta-secondary"
            >
              {slide.ctaSecondary.label}
            </Link>
          </div>
        </div>
      </div>

      {/* Navigation arrows - hidden on mobile */}
      <button
        onClick={prev}
        className="hidden md:flex absolute left-4 lg:left-8 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 text-white items-center justify-center hover:bg-white/20 transition-all"
        aria-label="Slide precedente"
        data-testid="hero-prev"
      >
        <ChevronLeft size={22} />
      </button>
      <button
        onClick={next}
        className="hidden md:flex absolute right-4 lg:right-8 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 text-white items-center justify-center hover:bg-white/20 transition-all"
        aria-label="Slide successiva"
        data-testid="hero-next"
      >
        <ChevronRight size={22} />
      </button>

      {/* Dots - above stats bar */}
      <div className="absolute bottom-[100px] md:bottom-[100px] left-1/2 -translate-x-1/2 z-20 flex items-center gap-3" role="tablist" aria-label="Seleziona slide">
        {HERO_SLIDES.map((_, i) => (
          <button
            key={i}
            onClick={() => goTo(i)}
            className={`transition-all duration-300 rounded-full ${
              i === current
                ? "w-10 h-2.5 bg-white"
                : "w-2.5 h-2.5 bg-white/40 hover:bg-white/60"
            }`}
            role="tab"
            aria-selected={i === current}
            aria-label={`Slide ${i + 1} di ${HERO_SLIDES.length}`}
            data-testid={`hero-dot-${i}`}
          />
        ))}
      </div>

      {/* Stats bar at bottom */}
      <div className="absolute bottom-0 left-0 right-0 z-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="hidden md:grid grid-cols-3 border-t border-white/10">
            {[
              { value: "80", label: "Anni di storia", icon: TrendingUp },
              { value: "15+", label: "Associazioni", icon: Users },
              { value: "20+", label: "Servizi", icon: Shield },
            ].map((stat, i) => (
              <div
                key={stat.label}
                className={`flex items-center gap-4 py-5 px-5 bg-black/20 backdrop-blur-sm ${i < 2 ? "border-r border-white/10" : ""}`}
              >
                <stat.icon size={20} className="text-white/50 shrink-0" aria-hidden="true" />
                <div>
                  <div className="text-white font-bold text-xl font-['Encode_Sans_Semi_Condensed']">{stat.value}</div>
                  <div className="text-white/50 text-xs">{stat.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function ServiceGrid() {
  return (
    <section className="py-20 bg-white" aria-labelledby="servizi-title" data-testid="service-grid">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#56B7E6] font-['Encode_Sans_Semi_Condensed']">Il sistema Confcommercio Roma</span>
          <h2 id="servizi-title" className="text-3xl md:text-4xl font-bold text-[var(--confcom-text-primary)] mt-3 font-['Encode_Sans_Semi_Condensed'] tracking-tight">
            I servizi per la tua impresa
          </h2>
          <p className="text-[var(--confcom-text-secondary)] mt-4 max-w-2xl mx-auto">
            Consulenza, formazione e servizi specialistici per far crescere la tua attività.
          </p>
        </div>
        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {SERVIZI_CATEGORIE.map((cat) => {
            const Icon = ICON_MAP[cat.icon] || Shield;
            return (
              <Link
                key={cat.id}
                to={`/servizi#${cat.id}`}
                className="group bg-white rounded-xl p-8 border border-[var(--confcom-border)] hover:border-[var(--confcom-blue)]/30 hover:shadow-lg transition-all"
                data-testid={`service-card-${cat.id}`}
              >
                <div className="w-12 h-12 bg-[var(--confcom-bg-blue-light)] rounded-xl flex items-center justify-center mb-5 group-hover:bg-[var(--confcom-blue)] transition-colors">
                  <Icon size={24} className="text-[var(--confcom-blue)] group-hover:text-white transition-colors" aria-hidden="true" />
                </div>
                <h3 className="text-lg font-semibold text-[var(--confcom-text-primary)] mb-3 font-['Encode_Sans_Semi_Condensed']">{cat.titolo}</h3>
                <p className="text-sm text-[var(--confcom-text-secondary)] leading-relaxed mb-4">{cat.descrizione}</p>
                <span className="inline-flex items-center gap-1 text-sm font-medium text-[var(--confcom-blue)] group-hover:gap-2 transition-all">
                  Scopri di più <ChevronRight size={16} aria-hidden="true" />
                </span>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function AssociationsSection() {
  return (
    <section className="py-20 bg-[#041E42]" aria-labelledby="associazioni-title" data-testid="associations-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between mb-12">
          <div>
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[var(--confcom-celeste-light)] font-['Encode_Sans_Semi_Condensed']">La nostra rete</span>
            <h2 id="associazioni-title" className="text-3xl md:text-4xl font-bold text-white mt-3 font-['Encode_Sans_Semi_Condensed'] tracking-tight">
              Le nostre Associazioni, Federazioni e Gruppi
            </h2>
          </div>
          <Link to="/associazioni" className="hidden md:inline-flex items-center gap-2 text-sm font-medium text-[var(--confcom-celeste-light)] hover:text-white hover:gap-3 transition-all" data-testid="associations-see-all">
            Vedi tutte <ArrowRight size={16} aria-hidden="true" />
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {['fipe','fimaa','professioni'].map((assId) => {
            const ass = ASSOC_LIST.find((a) => a.id === assId);
            if (!ass) return null;
            const color = ass.mercato ? MERCATO_HEX[ass.mercato] : "#007ACC";
            const badgeLabel = ass.mercato || ass.tipologia;
            const ASSOC_ICONS = {
              fipe: UtensilsCrossed, fimaa: Home, abiconf: Building2,
              assintel: Laptop, 'moda-italia': ShoppingBag, professioni: Briefcase,
              fida: Apple, fima: Palette, fedagro: Store,
            };
            const Icon = ASSOC_ICONS[ass.id] || Users;
            return (
              <Link
                key={ass.id}
                to={`/associazioni/${ass.id}`}
                className="group flex items-start gap-4 p-5 rounded-lg bg-white/[0.07] border border-white/10 hover:bg-white/[0.15] transition-all"
                data-testid={`association-${ass.id}`}
              >
                <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: color + "30" }}>
                  <Icon size={20} style={{ color }} aria-hidden="true" />
                </div>
                <div>
                  <span className="inline-block text-[10px] font-semibold text-white px-2 py-0.5 rounded mb-1.5" style={{ backgroundColor: color }}>{badgeLabel}</span>
                  <h3 className="text-sm font-semibold text-white mb-1 font-['Encode_Sans_Semi_Condensed'] leading-snug group-hover:text-[var(--confcom-celeste-light)] transition-colors">{ass.nome}</h3>
                  <p className="text-xs text-white/60 leading-relaxed">{ass.descrizione.slice(0, 80)}...</p>
                </div>
              </Link>
            );
          })}
        </div>
        <div className="mt-8 text-center md:hidden">
          <Link to="/associazioni" className="inline-flex items-center gap-2 text-sm font-medium text-[var(--confcom-celeste-light)]">
            Vedi tutte le associazioni <ArrowRight size={16} aria-hidden="true" />
          </Link>
        </div>
      </div>
    </section>
  );
}

function NewsSection() {
  return (
    <section className="py-20 bg-[var(--confcom-bg-subtle)]" aria-labelledby="news-title" data-testid="news-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between mb-12">
          <div>
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#56B7E6] font-['Encode_Sans_Semi_Condensed']">Resta aggiornato</span>
            <h2 id="news-title" className="text-3xl md:text-4xl font-bold text-[var(--confcom-text-primary)] mt-3 font-['Encode_Sans_Semi_Condensed'] tracking-tight">
              Ultime notizie
            </h2>
          </div>
          <Link to="/notizie" className="hidden md:inline-flex items-center gap-2 text-sm font-medium text-[var(--confcom-blue)] hover:gap-3 transition-all" data-testid="news-see-all">
            Tutte le notizie <ArrowRight size={16} aria-hidden="true" />
          </Link>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {NEWS_FULL.slice(0, 6).map((n) => (
            <Link key={n.id} to={`/notizie/${n.slug}`} className="group bg-white rounded-xl border border-[var(--confcom-border)] overflow-hidden hover:shadow-lg transition-all" data-testid={`news-card-${n.id}`}>
              <div className="h-44 overflow-hidden">
                <img src={n.image} alt={n.titolo} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
              </div>
              <div className="p-5">
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-xs font-medium text-[var(--confcom-blue)] bg-[var(--confcom-bg-blue-light)] px-2.5 py-1 rounded-md">{n.categoria}</span>
                  <time className="text-xs text-[var(--confcom-text-muted)]" dateTime={n.data}>{n.data}</time>
                </div>
                <h3 className="text-base font-semibold text-[var(--confcom-text-primary)] mb-2 font-['Encode_Sans_Semi_Condensed'] leading-snug group-hover:text-[var(--confcom-blue)] transition-colors line-clamp-2">
                  {n.titolo}
                </h3>
                <p className="text-sm text-[var(--confcom-text-secondary)] leading-relaxed line-clamp-2">{n.excerpt}</p>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function EventsPreview() {
  return (
    <section className="pt-12 pb-20 bg-[#EBF5FF]" aria-labelledby="events-title" data-testid="events-preview">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between mb-12">
          <div>
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#56B7E6] font-['Encode_Sans_Semi_Condensed']">In agenda</span>
            <h2 id="events-title" className="text-3xl md:text-4xl font-bold text-[var(--confcom-text-primary)] mt-3 font-['Encode_Sans_Semi_Condensed'] tracking-tight">
              Prossimi appuntamenti
            </h2>
          </div>
          <Link to="/eventi" className="hidden md:inline-flex items-center gap-2 text-sm font-medium text-[var(--confcom-blue)] hover:gap-3 transition-all" data-testid="events-see-all">
            Tutti gli appuntamenti <ArrowRight size={16} aria-hidden="true" />
          </Link>
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          {EVENTI_FULL.slice(0, 4).map((e) => (
            <Link key={e.id} to={`/eventi/${e.slug}`} className="group bg-white rounded-xl border border-[var(--confcom-border)] overflow-hidden hover:shadow-lg transition-all" data-testid={`event-preview-${e.id}`}>
              <div className="h-44 overflow-hidden relative">
                <img src={e.image} alt={e.titolo} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                <div className="absolute top-4 left-4 w-16 h-16 bg-[var(--confcom-blue)] rounded-xl flex flex-col items-center justify-center text-white shadow-lg">
                  <span className="text-2xl font-bold font-['Encode_Sans_Semi_Condensed'] leading-none">{e.giorno}</span>
                  <span className="text-xs uppercase font-medium">{e.mese}</span>
                </div>
              </div>
              <div className="p-5">
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-xs font-medium text-[var(--confcom-blue)] bg-[var(--confcom-bg-blue-light)] px-2.5 py-1 rounded-md">{e.tipo}</span>
                  <span className="text-xs text-[var(--confcom-text-muted)]">{e.data}</span>
                </div>
                <h3 className="text-base font-semibold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] leading-snug group-hover:text-[var(--confcom-blue)] transition-colors">
                  {e.titolo}
                </h3>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function SocialeBlock() {
  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="order-2 lg:order-1">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#56B7E6] font-['Encode_Sans_Semi_Condensed']">Chi Siamo</span>
            <h2 className="text-3xl md:text-4xl font-bold text-[var(--confcom-text-primary)] mt-3 mb-6 font-['Encode_Sans_Semi_Condensed'] tracking-tight">
              La Rappresentanza
            </h2>
            <p className="text-[var(--confcom-text-secondary)] leading-relaxed mb-4">
              La rappresentanza non è un concetto astratto: è la nostra missione quotidiana. Siamo la voce delle imprese del commercio, del turismo, dei servizi e delle professioni di Roma, in ogni sede istituzionale, politica e sociale.
            </p>
            <p className="text-[var(--confcom-text-secondary)] leading-relaxed mb-8">
              Difendiamo gli interessi degli associati, promuoviamo politiche favorevoli alla crescita e tuteliamo una concorrenza sana e leale per tutte le imprese del territorio.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/chi-siamo#servizi-rappresentanza" className="inline-flex items-center gap-2 bg-[var(--confcom-blue)] text-white px-7 py-3 rounded-md font-semibold hover:opacity-90 transition-opacity text-sm">
                Scopri come <ArrowRight size={16} aria-hidden="true" />
              </Link>
              <Link to="/chi-siamo" className="inline-flex items-center gap-2 border border-[var(--confcom-blue)] text-[var(--confcom-blue)] px-7 py-3 rounded-md font-medium hover:opacity-90 transition-opacity text-sm">
                Chi Siamo
              </Link>
            </div>
          </div>
          <div className="order-1 lg:order-2 rounded-2xl overflow-hidden">
            <img src="https://images.pexels.com/photos/7642001/pexels-photo-7642001.jpeg?auto=compress&cs=tinysrgb&w=1280" alt="Confcommercio Roma — La rappresentanza delle imprese" className="w-full h-[320px] object-cover" />
          </div>
        </div>
      </div>
    </section>
  );
}

function WelfareBlock() {
  return (
    <section className="pt-20 pb-10 bg-[#EBF5FF]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="rounded-2xl overflow-hidden">
            <img src="https://images.unsplash.com/photo-1573497620053-ea5300f94f21?w=800&q=80&auto=format" alt="Welfare aziendale" className="w-full h-[320px] object-cover" />
          </div>
          <div>
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#56B7E6] font-['Encode_Sans_Semi_Condensed']">Per le aziende e i dipendenti</span>
            <h2 className="text-3xl md:text-4xl font-bold text-[var(--confcom-text-primary)] mt-3 mb-6 font-['Encode_Sans_Semi_Condensed'] tracking-tight">
              Politiche di Welfare
            </h2>
            <p className="text-[var(--confcom-text-secondary)] leading-relaxed mb-4">
              Il benessere dei dipendenti è una leva strategica per la crescita dell'impresa. Confcommercio Roma supporta le aziende associate nella progettazione di piani welfare personalizzati: dall'assistenza sanitaria integrativa alla previdenza complementare, dai flexible benefit alla conciliazione vita-lavoro.
            </p>
            <p className="text-[var(--confcom-text-secondary)] leading-relaxed mb-8">
              Grazie ai nostri partner con Sanimpresa, Fon.Te., Ebit Lazio e ai servizi di consulenza dedicati, le imprese possono offrire benefit concreti ai propri collaboratori, migliorando il clima aziendale e la capacità di attrarre talenti.
            </p>
            <Link to="/area-lavoro-welfare/welfare-aziendale" className="inline-flex items-center gap-2 bg-[var(--confcom-blue)] text-white px-7 py-3 rounded-md font-medium hover:opacity-90 transition-opacity text-sm">
              Scopri il Welfare aziendale <ArrowRight size={16} aria-hidden="true" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

function CommunityBlock() {
  return (
    <section className="py-20 relative overflow-hidden">
      <div className="absolute inset-0">
        <img src="/marketplace-hero.jpg" alt="" className="w-full h-full object-cover" aria-hidden="true" />
        <div className="absolute inset-0" style={{ backgroundColor: "rgba(4, 30, 66, 0.92)" }} />
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <span className="inline-flex items-center gap-2 text-sm font-semibold text-white bg-white/15 px-4 py-2 rounded-full mb-6 w-fit">
              Prossimamente
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-white font-['Encode_Sans_Semi_Condensed'] tracking-tight leading-tight mb-6">
              Il Marketplace della Community Confcommercio Roma
            </h2>
            <p className="text-white/80 leading-relaxed mb-4">
              Immagina uno spazio digitale dove ogni associato può trovare ciò di cui ha bisogno e offrire ciò che sa fare meglio. Un luogo dove domanda e offerta tra imprese si incontrano in modo diretto, veloce e qualificato.
            </p>
            <p className="text-white/80 leading-relaxed mb-8">
              Stiamo costruendo una piattaforma esclusiva per i soci dove potrai pubblicare le esigenze della tua impresa — dalla ricerca di un fornitore affidabile alla necessità di un servizio specifico — e ricevere risposte concrete dalla rete di oltre 2.000 aziende associate.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/marketplace" className="inline-flex items-center gap-2 bg-white text-[var(--confcom-blue)] px-7 py-3 rounded-md font-bold hover:opacity-90 transition-opacity shadow-lg text-sm">
                Esplora il Marketplace <ArrowRight size={16} aria-hidden="true" />
              </Link>
              <Link to="/associati-ora" className="inline-flex items-center gap-2 border-2 border-white/40 text-white px-7 py-3 rounded-md font-semibold hover:opacity-90 transition-opacity text-sm">
                Associati ora
              </Link>
            </div>
          </div>
          <div className="relative pl-8">
            {/* Timeline line */}
            <div className="absolute left-[19px] top-6 bottom-6 w-[2px] bg-gradient-to-b from-[#56B7E6] via-[#56B7E6]/50 to-[#56B7E6]/20" />

            {[
              { titolo: "Pubblica il tuo bisogno", desc: "Cerchi un fornitore, un partner, un servizio? Descrivi la tua esigenza è la community ti risponde." },
              { titolo: "Offri le tue competenze", desc: "Fai conoscere i tuoi prodotti e servizi a migliaia di imprese del territorio. Nuove opportunità di business tra soci." },
              { titolo: "Soluzioni su misura", desc: "Ogni richiesta viene indirizzata ai soci più pertinenti. Risposte qualificate, non generiche." },
              { titolo: "Rete di fiducia", desc: "Tutte le imprese sono verificate e associate. Lavori solo con chi condivide i tuoi stessi valori." },
            ].map((item, i) => (
              <div key={item.titolo} className="relative mb-10 last:mb-0">
                {/* Dot on timeline */}
                <div className="absolute -left-8 top-5 w-10 h-10 bg-[#56B7E6] rounded-full flex items-center justify-center text-white font-bold text-sm font-['Encode_Sans_Semi_Condensed'] shadow-lg shadow-[#56B7E6]/30 z-10">
                  {i + 1}
                </div>
                {/* Card */}
                <div className="ml-8 bg-white/[0.08] backdrop-blur-sm border border-white/10 rounded-xl p-6">
                  <h3 className="text-base font-semibold text-white font-['Encode_Sans_Semi_Condensed'] mb-2">{item.titolo}</h3>
                  <p className="text-sm text-white/60 leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function CTASection() {
  return (
    <section className="py-20 relative overflow-hidden" data-testid="cta-section">
      <div className="absolute inset-0">
        <img src="/cta-background.jpg" alt="" className="w-full h-full object-cover" aria-hidden="true" />
        <div className="absolute inset-0" style={{ backgroundColor: "rgba(0, 59, 117, 0.88)" }} />
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
        <h2 className="text-3xl md:text-4xl font-bold text-white font-['Encode_Sans_Semi_Condensed'] tracking-tight mb-6">
          Entra a far parte del sistema Confcommercio Roma
        </h2>
        <p className="text-lg text-white/80 max-w-2xl mx-auto mb-8">
          Unisciti a migliaia di imprese che ogni giorno trovano in Confcommercio Roma un partner affidabile per la crescita è la rappresentanza.
        </p>
        <div className="flex flex-wrap justify-center gap-4">
          <Link
            to="/contatti"
            className="inline-flex items-center gap-2 bg-white text-[var(--confcom-blue)] px-8 py-4 rounded-md font-semibold hover:opacity-90 transition-opacity shadow-lg"
            data-testid="cta-contattaci"
          >
            Contattaci <ArrowRight size={18} aria-hidden="true" />
          </Link>
        </div>
      </div>
    </section>
  );
}

export default function HomePage() {
  return (
    <main id="main-content" data-testid="home-page">
      <HeroSection />
      <ServiceGrid />
      <AssociationsSection />
      <SocialeBlock />
      <WelfareBlock />
      <EventsPreview />
      {/* CommunityBlock (sezione Marketplace) rimossa dalla homepage su richiesta utente (#1938) — componente mantenuto nel codice sopra per eventuale riattivazione futura. Per riattivarla, rimuovere questo commento e ripristinare <CommunityBlock /> qui. */}
      <NewsSection />
      <CTASection />
    </main>
  );
}

